/**
 * WerkstattKiosk — Werkstatt-Cockpit mit Widget-Grid-Layout
 *
 * DESIGN: Grauer Hintergrund (#EAECEF), weiße Boxen, einheitlich graue Icons,
 *         Koop-Rot (#C41E3A) nur für Akzente/Buttons
 * LAYOUT: 3-Spalten Widget-Grid
 *   - Spalte 1: Fallübersicht (Werkstatt-Akten)
 *   - Spalte 2: Kalender (Tag/Woche/Monat) + Aufgaben
 *   - Spalte 3: Mietwagen (Im Aufbau) + Autoglas (Im Aufbau)
 * TOUCH: Alle Elemente min. 52px Höhe, größere Schriften für Monitor-Bedienung
 * GOOGLE CALENDAR: OAuth Flow über useGoogleCalendar Hook
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import {
  X, Search, ChevronLeft, ChevronRight, Calendar,
  Plus, Trash2, Maximize2, Minimize2, ChevronUp, ChevronDown,
  FileText, AlertCircle, CheckCircle2,
  RefreshCw, Settings, WifiOff, ListTodo, Car, GlassWater,
  Pencil, Clock, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { useGoogleCalendar, GoogleCalendarEvent } from '@/hooks/useGoogleCalendar';
import { supabase } from '@/lib/supabase';
import { localIsoDate } from '@/lib/date-utils';

// ─── Types ───────────────────────────────────────────────────────────────────

type KalenderAnsicht = 'tag' | 'woche' | 'monat';
type AufgabePrioritaet = 'normal' | 'hoch' | 'dringend';

interface KalenderTermin {
  id: string;
  datum: string;
  startStunde: number;
  dauerStunden: number;
  titel: string;
  kennzeichen: string;
  typ: string;
  mitarbeiter: string;
  googleEvent?: boolean;
}

interface Aufgabe {
  id: string;
  text: string;
  prioritaet: AufgabePrioritaet;
  erledigt: boolean;
  erstelltAm: string;
}

interface WerkstattAkte {
  akten_nummer: string;
  kunde_name: string;
  kennzeichen: string;
  status: string;
  fahrzeug: string;
  created_at: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const KOOP_ROT = '#C41E3A';



const WOCHENTAGE = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'];
const WOCHENTAGE_LANG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const ZEITSLOTS = Array.from({ length: 12 }, (_, i) => i + 7); // 7:00 – 18:00

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(d: Date): string {
  return `${WOCHENTAGE_LANG[(d.getDay() + 6) % 7]}, ${d.getDate()}. ${MONATE[d.getMonth()]} ${d.getFullYear()}`;
}

function getMonday(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.getFullYear(), d.getMonth(), diff);
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getMonthDays(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const startDay = (first.getDay() + 6) % 7;
  const days: Date[] = [];
  for (let i = -startDay; i < 42 - startDay; i++) {
    days.push(new Date(year, month, 1 + i));
  }
  return days;
}

// Convert Google Calendar event to KalenderTermin
function googleEventToTermin(event: GoogleCalendarEvent): KalenderTermin {
  const start = new Date(event.start);
  const end = new Date(event.end);
  const dauer = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  return {
    id: event.id,
    datum: localIsoDate(start),
    startStunde: start.getHours() + start.getMinutes() / 60,
    dauerStunden: Math.max(0.5, dauer),
    titel: event.summary || 'Termin',
    kennzeichen: '',
    typ: 'Google',
    mitarbeiter: event.creator || '',
    googleEvent: true,
  };
}

// ─── Termin Detail Modal ────────────────────────────────────────────────────

function TerminDetailModal({ termin, onClose, onEdit, onDelete }: {
  termin: KalenderTermin;
  onClose: () => void;
  onEdit: (t: KalenderTermin) => void;
  onDelete: (t: KalenderTermin) => void;
}) {
  const startH = Math.floor(termin.startStunde);
  const startM = Math.round((termin.startStunde - startH) * 60);
  const endH = Math.floor(termin.startStunde + termin.dauerStunden);
  const endM = Math.round(((termin.startStunde + termin.dauerStunden) - endH) * 60);
  const timeStr = `${startH.toString().padStart(2,'0')}:${startM.toString().padStart(2,'0')} – ${endH.toString().padStart(2,'0')}:${endM.toString().padStart(2,'0')}`;
  const dateObj = new Date(termin.datum + 'T12:00:00');
  const dateStr = dateObj.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="h-1.5 w-full" style={{ backgroundColor: termin.googleEvent ? '#1a73e8' : KOOP_ROT }} />
        <div className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-bold text-gray-900 flex-1">{termin.titel}</h3>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 flex-shrink-0">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>{dateStr} · {timeStr}</span>
          </div>
          {termin.kennzeichen && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Car className="w-4 h-4 flex-shrink-0" />
              <span>{termin.kennzeichen}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: termin.googleEvent ? '#1a73e8' : KOOP_ROT }}>
              {termin.googleEvent ? 'Google Calendar' : 'Werkstatt'}
            </span>
            {termin.typ && termin.typ !== 'Google' && termin.typ !== 'Werkstatt' && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">{termin.typ}</span>
            )}
          </div>
          <div className="flex gap-2 pt-3 border-t border-gray-100">
            <button onClick={() => { onClose(); onEdit(termin); }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all" style={{ minHeight: '52px' }}>
              <Pencil className="w-4 h-4" /> Bearbeiten
            </button>
            <button onClick={() => onDelete(termin)}
              className="flex items-center justify-center w-14 rounded-xl text-sm font-bold border border-red-200 text-red-500 hover:bg-red-50 transition-all flex-shrink-0" style={{ minHeight: '52px' }}
              title="Termin löschen">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Termin Edit Modal ──────────────────────────────────────────────────────

function TerminEditModal({ termin, onClose, onSave }: {
  termin: KalenderTermin;
  onClose: () => void;
  onSave: (updated: { id: string; titel: string; datum: string; startStunde: number; dauerStunden: number; googleEvent?: boolean }) => Promise<void>;
}) {
  const [titel, setTitel] = useState(termin.titel);
  const startH = Math.floor(termin.startStunde);
  const startM = Math.round((termin.startStunde - startH) * 60);
  const endTotal = termin.startStunde + termin.dauerStunden;
  const endH = Math.floor(endTotal);
  const endM = Math.round((endTotal - endH) * 60);
  const [datum, setDatum] = useState(termin.datum);
  const [startTime, setStartTime] = useState(`${startH.toString().padStart(2,'0')}:${startM.toString().padStart(2,'0')}`);
  const [endTime, setEndTime] = useState(`${endH.toString().padStart(2,'0')}:${endM.toString().padStart(2,'0')}`);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titel.trim()) return;
    setSaving(true);
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const newStart = sh + sm / 60;
    const newEnd = eh + em / 60;
    const dauer = Math.max(0.25, newEnd - newStart);
    await onSave({ id: termin.id, titel, datum, startStunde: newStart, dauerStunden: dauer, googleEvent: termin.googleEvent });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: termin.googleEvent ? '#1a73e815' : KOOP_ROT + '15' }}>
              <Calendar className="w-4 h-4" style={{ color: termin.googleEvent ? '#1a73e8' : KOOP_ROT }} />
            </div>
            <h3 className="text-sm font-bold text-gray-900">Termin bearbeiten</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Titel *</label>
            <input type="text" value={titel} onChange={e => setTitel(e.target.value)} required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" style={{ height: '52px' }} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Datum</label>
            <input type="date" value={datum} onChange={e => setDatum(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" style={{ height: '52px' }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Start</label>
              <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" style={{ height: '52px' }} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Ende</label>
              <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" style={{ height: '52px' }} />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl text-sm font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all" style={{ height: '52px' }}>Abbrechen</button>
            <button type="submit" disabled={saving || !titel.trim()}
              className="flex-1 px-4 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: KOOP_ROT, height: '52px' }}>
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Termin Create Modal ────────────────────────────────────────────────────

function TerminCreateModal({ onClose, onSave }: {
  onClose: () => void;
  onSave: (data: { titel: string; datum: string; startStunde: number; dauerStunden: number }) => Promise<void>;
}) {
  const now = new Date();
  const nextHour = now.getHours() + 1;
  const [titel, setTitel] = useState('');
  const [datum, setDatum] = useState(localIsoDate(now));
  const [startTime, setStartTime] = useState(`${nextHour.toString().padStart(2,'0')}:00`);
  const [endTime, setEndTime] = useState(`${(nextHour + 1).toString().padStart(2,'0')}:00`);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titel.trim()) return;
    setSaving(true);
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const newStart = sh + sm / 60;
    const newEnd = eh + em / 60;
    const dauer = Math.max(0.25, newEnd - newStart);
    await onSave({ titel, datum, startStunde: newStart, dauerStunden: dauer });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: KOOP_ROT + '15' }}>
              <Plus className="w-4 h-4" style={{ color: KOOP_ROT }} />
            </div>
            <h3 className="text-sm font-bold text-gray-900">Neuer Termin</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Titel *</label>
            <input type="text" value={titel} onChange={e => setTitel(e.target.value)} required
              placeholder="z.B. Besichtigung VW Golf"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" style={{ height: '52px' }} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Datum</label>
            <input type="date" value={datum} onChange={e => setDatum(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" style={{ height: '52px' }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Start</label>
              <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" style={{ height: '52px' }} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Ende</label>
              <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" style={{ height: '52px' }} />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl text-sm font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all" style={{ height: '52px' }}>Abbrechen</button>
            <button type="submit" disabled={saving || !titel.trim()}
              className="flex-1 px-4 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: KOOP_ROT, height: '52px' }}>
              {saving ? 'Erstellen...' : 'Termin erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Widget Box Component ────────────────────────────────────────────────────

function WidgetBox({
  title, icon, children, headerRight, className = '', collapsed, onToggleCollapse, onExpand,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
  className?: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onExpand?: () => void;
}) {
  return (
    <div className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 flex flex-col ${className}`}>
      {/* Widget Header — Touch-optimiert: min 52px */}
      <div className="flex items-center justify-between px-5 border-b border-gray-100" style={{ minHeight: '52px' }}>
        <div className="flex items-center gap-3">
          <span className="text-gray-500">{icon}</span>
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">{title}</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {headerRight}
          {onExpand && (
            <button onClick={onExpand} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="Erweitern">
              <Maximize2 size={16} />
            </button>
          )}
          {onToggleCollapse && (
            <button onClick={onToggleCollapse} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title={collapsed ? 'Aufklappen' : 'Einklappen'}>
              {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          )}
        </div>
      </div>
      {/* Widget Body */}
      {!collapsed && (
        <div className="flex-1 overflow-hidden">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Fallübersicht Widget ────────────────────────────────────────────────────

function FalluebersichtWidget() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string>('alle');
  const [akten, setAkten] = useState<WerkstattAkte[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAkten = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('akten')
        .select('akten_nummer, kunde_name, kennzeichen, status, fahrzeug, created_at')
        .eq('business_unit', 'WK')
        .order('created_at', { ascending: false })
        .limit(50);
      setAkten(data || []);
      setLoading(false);
    };
    fetchAkten();
  }, []);

  const filtered = useMemo(() => {
    return akten.filter(a => {
      const matchSearch = !search || (a.kunde_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (a.kennzeichen || '').toLowerCase().includes(search.toLowerCase()) ||
        (a.akten_nummer || '').toLowerCase().includes(search.toLowerCase());
      const matchFilter = filter === 'alle' ||
        (filter === 'offen' && a.status === 'offen') ||
        (filter === 'arbeit' && a.status === 'in_bearbeitung') ||
        (filter === 'fertig' && a.status === 'abgeschlossen');
      return matchSearch && matchFilter;
    });
  }, [akten, search, filter]);

  const statusLabel = (s: string) => {
    switch (s) {
      case 'offen': return { text: 'Offen', color: '#D97706', bg: '#FEF3C7' };
      case 'in_bearbeitung': return { text: 'In Arbeit', color: '#1D4ED8', bg: '#DBEAFE' };
      case 'abgeschlossen': return { text: 'Fertig', color: '#059669', bg: '#D1FAE5' };
      default: return { text: s, color: '#6B7280', bg: '#F3F4F6' };
    }
  };

  return (
    <div className="p-4 flex flex-col gap-3">
      {/* Search — Touch: min 52px */}
      <div className="relative">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Kennzeichen oder Name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-11 pr-4 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:border-gray-300 focus:ring-2 focus:ring-gray-200 outline-none transition-colors"
          style={{ height: '52px' }}
        />
      </div>
      {/* Filter — Touch: min 44px */}
      <div className="flex gap-2">
        {[
          { key: 'alle', label: 'Alle' },
          { key: 'offen', label: 'Offen' },
          { key: 'arbeit', label: 'In Arbeit' },
          { key: 'fertig', label: 'Fertig' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`flex-1 text-sm font-semibold rounded-lg transition-colors cursor-pointer`}
            style={{
              height: '44px',
              backgroundColor: filter === f.key ? KOOP_ROT : '#F3F4F6',
              color: filter === f.key ? 'white' : '#6B7280',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>
      {/* Liste — Touch: min 60px pro Zeile */}
      <div className="flex flex-col">
        {filtered.map((akte, i) => {
          const st = statusLabel(akte.status);
          return (
            <div
              key={akte.akten_nummer}
              className={`flex items-center gap-3 px-3 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100 last:border-0 ${
                i % 2 === 1 ? 'bg-gray-50/50' : ''
              }`}
              style={{ minHeight: '64px' }}
              onClick={() => toast.info(`Akte ${akte.akten_nummer} öffnen`)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-mono text-gray-400">{akte.akten_nummer}</span>
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ color: st.color, backgroundColor: st.bg }}
                  >
                    {st.text}
                  </span>
                </div>
                <p className="text-base font-semibold text-gray-800 truncate">{akte.kunde_name}</p>
                <p className="text-sm text-gray-500">{akte.kennzeichen} — {akte.fahrzeug}</p>
              </div>
              <ChevronRight size={18} className="text-gray-300 flex-shrink-0" />
            </div>
          );
        })}
        {loading && (
          <p className="text-sm text-gray-400 text-center py-8">Lade Fälle...</p>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">{akten.length === 0 ? 'Keine Werkstatt-Akten vorhanden' : 'Keine Fälle gefunden'}</p>
        )}
      </div>
    </div>
  );
}

// ─── Collision Detection ────────────────────────────────────────────────────

function computeWerkstattCollision(termine: KalenderTermin[]): Array<{ termin: KalenderTermin; column: number; totalColumns: number }> {
  if (termine.length === 0) return [];
  const sorted = [...termine].sort((a, b) => a.startStunde - b.startStunde);
  const groups: KalenderTermin[][] = [];
  let currentGroup: KalenderTermin[] = [];
  let groupEnd = -Infinity;
  for (const t of sorted) {
    const end = t.startStunde + t.dauerStunden;
    if (t.startStunde < groupEnd) {
      currentGroup.push(t);
      groupEnd = Math.max(groupEnd, end);
    } else {
      if (currentGroup.length) groups.push(currentGroup);
      currentGroup = [t];
      groupEnd = end;
    }
  }
  if (currentGroup.length) groups.push(currentGroup);
  const result: Array<{ termin: KalenderTermin; column: number; totalColumns: number }> = [];
  for (const group of groups) {
    const cols: KalenderTermin[][] = [];
    for (const t of group) {
      let placed = false;
      for (const col of cols) {
        const lastInCol = col[col.length - 1];
        if (t.startStunde >= lastInCol.startStunde + lastInCol.dauerStunden) {
          col.push(t); placed = true; break;
        }
      }
      if (!placed) cols.push([t]);
    }
    for (let ci = 0; ci < cols.length; ci++) {
      for (const t of cols[ci]) {
        result.push({ termin: t, column: ci, totalColumns: cols.length });
      }
    }
  }
  return result;
}

// ─── Kalender Widget ─────────────────────────────────────────────────────────

function KalenderWidget({ googleEvents, onEventClick }: { googleEvents: GoogleCalendarEvent[]; onEventClick?: (t: KalenderTermin) => void }) {
  const [ansicht, setAnsicht] = useState<KalenderAnsicht>(typeof window !== 'undefined' && window.innerWidth < 640 ? 'tag' : 'woche');

  // Listen for window resize to update default view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640 && ansicht === 'woche') {
        setAnsicht('tag');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [ansicht]);

  // Current time for time line
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  const [datum, setDatum] = useState(new Date());

  // Supabase Termine für Werkstatt
  const [supabaseTermine, setSupabaseTermine] = useState<KalenderTermin[]>([]);
  useEffect(() => {
    const fetchTermine = async () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
      const end = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString();
      const { data } = await supabase
        .from('termine')
        .select('id, titel, beginn, ende, business_unit')
        .eq('business_unit', 'WK')
        .gte('beginn', start)
        .lte('beginn', end)
        .order('beginn');
      if (data) {
        setSupabaseTermine(data.map(t => {
          const s = new Date(t.beginn);
          const e = t.ende ? new Date(t.ende) : new Date(s.getTime() + 3600000);
          return {
            id: t.id,
            datum: localIsoDate(s),
            startStunde: s.getHours() + s.getMinutes() / 60,
            dauerStunden: Math.max(0.25, (e.getTime() - s.getTime()) / 3600000),
            titel: t.titel || 'Termin',
            kennzeichen: '',
            typ: 'Werkstatt',
            mitarbeiter: '',
          };
        }));
      }
    };
    fetchTermine();
  }, []);

  // Merge supabase + google events (no demo data)
  const alleTermine = useMemo(() => {
    const googleTermine = googleEvents.map(googleEventToTermin);
    return [...supabaseTermine, ...googleTermine];
  }, [supabaseTermine, googleEvents]);

  // Navigation
  const navPrev = () => {
    const d = new Date(datum);
    if (ansicht === 'tag') d.setDate(d.getDate() - 1);
    else if (ansicht === 'woche') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setDatum(d);
  };
  const navNext = () => {
    const d = new Date(datum);
    if (ansicht === 'tag') d.setDate(d.getDate() + 1);
    else if (ansicht === 'woche') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setDatum(d);
  };
  const navHeute = () => setDatum(new Date());

  // Title
  const title = useMemo(() => {
    if (ansicht === 'tag') return formatDate(datum);
    if (ansicht === 'woche') {
      const mon = getMonday(datum);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      return `${mon.getDate()}. – ${sun.getDate()}. ${MONATE[sun.getMonth()]} ${sun.getFullYear()}`;
    }
    return `${MONATE[datum.getMonth()]} ${datum.getFullYear()}`;
  }, [ansicht, datum]);

  // Termine für einen Tag
  const termineAmTag = (d: Date) => alleTermine.filter(t => t.datum === localIsoDate(d));

  // ── Monatsansicht (responsive, fills container)
  const MonatsView = () => {
    const days = getMonthDays(datum.getFullYear(), datum.getMonth());
    const today = new Date();
    const numWeeks = Math.ceil(days.length / 7);
    return (
      <div className="flex flex-col h-full">
        {/* Wochentag-Header */}
        <div className="grid grid-cols-7 flex-shrink-0">
          {WOCHENTAGE.map(t => (
            <div key={t} className="text-center text-[10px] font-bold text-gray-400 uppercase py-1.5">{t}</div>
          ))}
        </div>
        {/* Tage - fills remaining space */}
        <div className="grid grid-cols-7 flex-1">
          {days.map((d, i) => {
            const isCurrentMonth = d.getMonth() === datum.getMonth();
            const isToday = isSameDay(d, today);
            const termine = termineAmTag(d);
            return (
              <div
                key={i}
                className={`border-t border-r border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 active:bg-gray-100 flex flex-col items-center p-0.5 overflow-hidden`}
                onClick={() => { setDatum(d); setAnsicht('tag'); }}
              >
                <span
                  className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${
                    isToday ? 'text-white' : isCurrentMonth ? 'text-gray-700' : 'text-gray-300'
                  }`}
                  style={isToday ? { backgroundColor: KOOP_ROT } : {}}
                >
                  {d.getDate()}
                </span>
                {termine.length > 0 && (
                  <div className="flex flex-wrap gap-0.5 mt-0.5 justify-center overflow-hidden">
                    {termine.slice(0, 2).map((t, ti) => (
                      <div
                        key={ti}
                        className="rounded-sm text-[8px] font-medium px-0.5 text-white truncate"
                        style={{ backgroundColor: t.googleEvent ? '#1a73e8' : KOOP_ROT, maxWidth: '100%' }}
                        title={t.titel}
                      >
                        {t.titel.length > 7 ? t.titel.slice(0, 7) + '…' : t.titel}
                      </div>
                    ))}
                    {termine.length > 2 && (
                      <div className="text-[8px] text-gray-400">+{termine.length - 2}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Wochenansicht mit Collision Detection
  const WochenView = () => {
    const wochenstart = getMonday(datum);
    const wochentage = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(wochenstart);
      d.setDate(d.getDate() + i);
      return d;
    });
    
    // Fallback for mobile since it's hidden in UI but could be active
    if (typeof window !== 'undefined' && window.innerWidth < 640) {
      return <div className="p-4 text-center text-gray-500">Wochenansicht ist auf kleinen Bildschirmen nicht verfügbar. Bitte nutzen Sie die Tagesansicht.</div>;
    }
    const today = new Date();
    const isCurrentWeek = wochentage.some(d => isSameDay(d, today));
    const START_H = ZEITSLOTS[0];
    const END_H = ZEITSLOTS[ZEITSLOTS.length - 1] + 1;
    const TOTAL_H = END_H - START_H;
    const SLOT_PX = 56; // px per hour
    const TOTAL_PX = TOTAL_H * SLOT_PX;

    const getTimeLinePx = () => {
      const now = currentTime;
      const currentHour = now.getHours() + now.getMinutes() / 60;
      if (currentHour < START_H || currentHour > END_H) return null;
      return ((currentHour - START_H) / TOTAL_H) * TOTAL_PX;
    };
    const timeLinePx = isCurrentWeek ? getTimeLinePx() : null;

    return (
      <div className="flex flex-col h-full overflow-auto">
        {/* Header */}
        <div className="grid grid-cols-[50px_repeat(7,1fr)] border-b border-gray-100 flex-shrink-0 sticky top-0 bg-white z-20">
          <div className="py-2" />
          {wochentage.map((d, i) => (
            <div
              key={i}
              className={`text-center py-2 cursor-pointer transition-colors hover:bg-gray-50 ${isSameDay(d, today) ? 'bg-red-50/40' : ''}`}
              onClick={() => { setDatum(d); setAnsicht('tag'); }}
            >
              <div className="text-[10px] font-bold text-gray-400 uppercase">{WOCHENTAGE[i]}</div>
              <div
                className={`text-sm font-bold w-8 h-8 mx-auto flex items-center justify-center rounded-full ${
                  isSameDay(d, today) ? 'text-white' : 'text-gray-700'
                }`}
                style={isSameDay(d, today) ? { backgroundColor: KOOP_ROT } : {}}
              >
                {d.getDate()}
              </div>
            </div>
          ))}
        </div>
        {/* Zeitgitter */}
        <div className="relative flex-shrink-0" style={{ height: TOTAL_PX }}>
          {/* Stunden-Linien */}
          {ZEITSLOTS.map((h) => (
            <div key={h} className="absolute left-0 right-0 grid grid-cols-[50px_repeat(7,1fr)]" style={{ top: ((h - START_H) / TOTAL_H) * TOTAL_PX }}>
              <div className="text-[10px] text-gray-300 font-bold pr-2 pt-0.5 text-right">{h}:00</div>
              {wochentage.map((_, di) => (
                <div key={di} className="border-l border-t border-gray-100" style={{ height: SLOT_PX }} />
              ))}
            </div>
          ))}
          {/* Events mit Collision Detection */}
          {wochentage.map((d, di) => {
            const isToday = isSameDay(d, today);
            const dayTermine = termineAmTag(d);
            const positioned = computeWerkstattCollision(dayTermine);
            // Column offset: 50px for time labels + (di / 7) of remaining width
            return (
              <div
                key={di}
                className={`absolute top-0 bottom-0 ${isToday ? 'bg-red-50/20' : ''}`}
                style={{
                  left: `calc(50px + ${(di / 7) * (100 - 50 / (window.innerWidth || 800) * 100)}%)`,
                  width: `calc((100% - 50px) / 7)`,
                  pointerEvents: 'none',
                }}
              >
                {positioned.map(({ termin: t, column, totalColumns }) => {
                  const top = ((t.startStunde - START_H) / TOTAL_H) * TOTAL_PX;
                  const height = Math.max(18, (t.dauerStunden / TOTAL_H) * TOTAL_PX - 2);
                  const GAP = 2;
                  const w = `calc(${100 / totalColumns}% - ${GAP}px)`;
                  const left = `calc(${(column / totalColumns) * 100}% + ${column > 0 ? GAP / 2 : 0}px)`;
                  return (
                    <div
                      key={t.id}
                      className="absolute rounded text-[9px] font-semibold px-1 py-0.5 cursor-pointer hover:opacity-90 hover:brightness-110 transition-all overflow-hidden shadow-sm hover:shadow-md"
                      style={{
                        top, height, left, width: w,
                        backgroundColor: t.googleEvent ? '#1a73e8' : KOOP_ROT,
                        color: 'white',
                        pointerEvents: 'auto',
                        zIndex: 5 + column,
                      }}
                      title={`${t.titel}${t.kennzeichen ? ' · ' + t.kennzeichen : ''}`}
                      onClick={() => onEventClick?.(t)}
                    >
                      <span className="truncate block">{t.titel}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {/* Current Time Line */}
          {timeLinePx !== null && (
            <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: timeLinePx }}>
              <div className="flex items-center">
                <div className="w-[50px] text-right pr-1">
                  <span className="text-[8px] font-bold text-[#C41E3A] bg-red-50 px-0.5 rounded">
                    {currentTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="flex-1 h-[2px] bg-[#C41E3A] relative">
                  <div className="absolute -left-1 -top-[3px] w-2 h-2 rounded-full bg-[#C41E3A]" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Tagesansicht mit Collision Detection
  const TagView = () => {
    const termine = termineAmTag(datum);
    const today = new Date();
    const isToday = isSameDay(datum, today);
    const START_H = ZEITSLOTS[0];
    const END_H = ZEITSLOTS[ZEITSLOTS.length - 1] + 1;
    const TOTAL_H = END_H - START_H;
    const SLOT_PX = 64;
    const TOTAL_PX = TOTAL_H * SLOT_PX;
    const formatTime = (h: number) => {
      const hours = Math.floor(h);
      const mins = Math.round((h - hours) * 60);
      return `${hours}:${mins.toString().padStart(2, '0')}`;
    };
    const getTimeLinePx = () => {
      const now = currentTime;
      const currentHour = now.getHours() + now.getMinutes() / 60;
      if (currentHour < START_H || currentHour > END_H) return null;
      return ((currentHour - START_H) / TOTAL_H) * TOTAL_PX;
    };
    const timeLinePx = isToday ? getTimeLinePx() : null;
    const positioned = computeWerkstattCollision(termine);

    return (
      <div className="overflow-auto" style={{ maxHeight: '100%' }}>
        <div className="relative" style={{ height: TOTAL_PX }}>
          {/* Stunden-Linien */}
          {ZEITSLOTS.map(h => (
            <div key={h} className="absolute left-0 right-0 border-t border-gray-100 flex" style={{ top: ((h - START_H) / TOTAL_H) * TOTAL_PX }}>
              <div className="text-[10px] text-gray-300 font-bold w-12 pt-0.5 text-right flex-shrink-0 pr-2">{h}:00</div>
            </div>
          ))}
          {/* Events mit Collision Detection */}
          <div className="absolute top-0 bottom-0" style={{ left: 52, right: 4 }}>
            {positioned.map(({ termin: t, column, totalColumns }) => {
              const top = ((t.startStunde - START_H) / TOTAL_H) * TOTAL_PX;
              const height = Math.max(28, (t.dauerStunden / TOTAL_H) * TOTAL_PX - 2);
              const GAP = 3;
              const w = `calc(${100 / totalColumns}% - ${GAP}px)`;
              const left = `calc(${(column / totalColumns) * 100}% + ${column > 0 ? GAP / 2 : 0}px)`;
              return (
                <div
                  key={t.id}
                  className="absolute rounded px-2 py-1 cursor-pointer hover:opacity-90 hover:brightness-110 transition-all overflow-hidden shadow-sm hover:shadow-md"
                  style={{
                    top, height, left, width: w,
                    backgroundColor: t.googleEvent ? '#1a73e8' : KOOP_ROT,
                    color: 'white',
                    zIndex: 5 + column,
                  }}
                  onClick={() => onEventClick?.(t)}
                >
                  <div className="text-xs font-bold truncate">{t.titel}</div>
                  {height > 36 && (
                    <div className="text-[10px] opacity-80">
                      {formatTime(t.startStunde)} – {formatTime(t.startStunde + t.dauerStunden)}
                      {t.kennzeichen && ` · ${t.kennzeichen}`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Keine Termine */}
          {termine.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
              Keine Termine
            </div>
          )}
          {/* Current Time Line */}
          {timeLinePx !== null && (
            <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: timeLinePx }}>
              <div className="flex items-center">
                <div className="w-12 text-right pr-1">
                  <span className="text-[8px] font-bold text-[#C41E3A]">{currentTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div className="flex-1 h-[2px] bg-[#C41E3A] relative">
                  <div className="absolute -left-1 -top-[3px] w-2 h-2 rounded-full bg-[#C41E3A]" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );

  };

  return (
    <div className="flex flex-col h-full">
      {/* Kalender-Navigation — Touch: min 52px */}
      <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-2 border-b border-gray-100 gap-2 sm:gap-0" style={{ minHeight: '52px' }}>
        <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
          <div className="flex items-center gap-2">
            <button
            onClick={navPrev}
            className="p-2.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={navNext}
            className="p-2.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <ChevronRight size={20} />
          </button>
            <span className="text-sm font-bold text-gray-700 ml-1">{title}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 w-full sm:w-auto justify-center sm:justify-end">
          <button
            onClick={navHeute}
            className="px-3 text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            style={{ height: '40px' }}
          >
            Heute
          </button>
          {(['tag', 'woche', 'monat'] as KalenderAnsicht[]).map(a => (
            <button
              key={a}
              onClick={() => setAnsicht(a)}
              className={`px-3 text-sm font-semibold rounded-lg transition-colors capitalize ${a === 'woche' ? 'hidden sm:block' : ''}`}
              style={{
                height: '40px',
                backgroundColor: ansicht === a ? KOOP_ROT : '#F3F4F6',
                color: ansicht === a ? 'white' : '#6B7280',
              }}
            >
              {a === 'tag' ? 'Tag' : a === 'woche' ? 'Woche' : 'Monat'}
            </button>
          ))}
        </div>
      </div>
      {/* Kalender-Inhalt - no scroll, fills remaining space */}
      <div className="flex-1 min-h-0">
        {ansicht === 'monat' && <MonatsView />}
        {ansicht === 'woche' && <WochenView />}
        {ansicht === 'tag' && <TagView />}
      </div>
    </div>
  );
}

// ─── Aufgaben Widget ─────────────────────────────────────────────────────────

function AufgabenWidget() {
  const [aufgaben, setAufgaben] = useState<Aufgabe[]>(() => {
    try {
      const s = localStorage.getItem('werkstatt_aufgaben');
      return s ? JSON.parse(s) : [];
    } catch { return []; }
  });
  const [neueAufgabe, setNeueAufgabe] = useState('');
  const [neuePrioritaet, setNeuePrioritaet] = useState<AufgabePrioritaet>('normal');

  useEffect(() => {
    localStorage.setItem('werkstatt_aufgaben', JSON.stringify(aufgaben));
  }, [aufgaben]);

  const addAufgabe = () => {
    if (!neueAufgabe.trim()) return;
    setAufgaben(prev => [...prev, {
      id: `a-${Date.now()}`,
      text: neueAufgabe.trim(),
      prioritaet: neuePrioritaet,
      erledigt: false,
      erstelltAm: localIsoDate(new Date()),
    }]);
    setNeueAufgabe('');
    toast.success('Aufgabe hinzugefügt');
  };

  const toggleAufgabe = (id: string) => {
    setAufgaben(prev => prev.map(a => a.id === id ? { ...a, erledigt: !a.erledigt } : a));
  };

  const deleteAufgabe = (id: string) => {
    setAufgaben(prev => prev.filter(a => a.id !== id));
    toast.info('Aufgabe gelöscht');
  };

  const prioritaetStyle = (p: AufgabePrioritaet) => {
    switch (p) {
      case 'dringend': return { color: KOOP_ROT, bg: '#FEE2E2', label: 'Dringend' };
      case 'hoch': return { color: '#D97706', bg: '#FEF3C7', label: 'Hoch' };
      default: return { color: '#6B7280', bg: '#F3F4F6', label: 'Normal' };
    }
  };

  const offene = aufgaben.filter(a => !a.erledigt);
  const erledigte = aufgaben.filter(a => a.erledigt);

  return (
    <div className="p-4 flex flex-col gap-3">
      {/* Eingabe — Touch: min 52px */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          placeholder="Neue Aufgabe hinzufügen..."
          value={neueAufgabe}
          onChange={(e) => setNeueAufgabe(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addAufgabe()}
          className="flex-1 px-4 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:border-gray-300 focus:ring-2 focus:ring-gray-200 outline-none transition-colors"
          style={{ height: '52px' }}
        />
        <select
          value={neuePrioritaet}
          onChange={(e) => setNeuePrioritaet(e.target.value as AufgabePrioritaet)}
          className="px-3 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white outline-none cursor-pointer"
          style={{ height: '52px' }}
        >
          <option value="normal">Normal</option>
          <option value="hoch">Hoch</option>
          <option value="dringend">Dringend</option>
        </select>
        <button
          onClick={addAufgabe}
          className="px-4 rounded-lg text-white transition-colors hover:opacity-90 flex items-center gap-2 font-semibold text-sm"
          style={{ backgroundColor: KOOP_ROT, height: '52px' }}
        >
          <Plus size={18} />
        </button>
      </div>
      {/* Liste — Touch: min 56px pro Zeile */}
      <div className="flex flex-col gap-1">
        {offene.map(a => {
          const ps = prioritaetStyle(a.prioritaet);
          return (
            <div key={a.id} className="flex items-center gap-3 px-3 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors group cursor-pointer" style={{ minHeight: '56px' }}>
              <button
                onClick={() => toggleAufgabe(a.id)}
                className="w-6 h-6 rounded-md border-2 border-gray-300 hover:border-gray-400 flex-shrink-0 transition-colors"
              />
              <span className="flex-1 text-sm font-medium text-gray-700 truncate">{a.text}</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0" style={{ color: ps.color, backgroundColor: ps.bg }}>
                {ps.label}
              </span>
              <button onClick={() => deleteAufgabe(a.id)} className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-500 transition-all rounded-lg hover:bg-red-50">
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
        {erledigte.length > 0 && (
          <>
            <div className="text-sm text-gray-400 px-3 pt-2 font-medium">Erledigt ({erledigte.length})</div>
            {erledigte.map(a => (
              <div key={a.id} className="flex items-center gap-3 px-3 rounded-lg hover:bg-gray-50 transition-colors group cursor-pointer opacity-50" style={{ minHeight: '48px' }}>
                <button
                  onClick={() => toggleAufgabe(a.id)}
                  className="w-6 h-6 rounded-md border-2 flex-shrink-0 flex items-center justify-center"
                  style={{ borderColor: KOOP_ROT, backgroundColor: KOOP_ROT + '20' }}
                >
                  <CheckCircle2 size={14} style={{ color: KOOP_ROT }} />
                </button>
                <span className="flex-1 text-sm text-gray-400 line-through truncate">{a.text}</span>
                <button onClick={() => deleteAufgabe(a.id)} className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-500 transition-all">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </>
        )}
        {aufgaben.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">Keine Aufgaben</p>
        )}
      </div>
    </div>
  );
}

// ─── Im Aufbau Widget ────────────────────────────────────────────────────────

function ImAufbauWidget({ title, description }: { title: string; description: string }) {
  return (
    <div className="p-6 flex flex-col items-center justify-center text-center gap-4 h-full">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
        <AlertCircle size={32} className="text-gray-400" />
      </div>
      <div>
        <span
          className="inline-block text-xs font-bold px-3 py-1 rounded-full text-white mb-3"
          style={{ backgroundColor: '#D97706' }}
        >
          IM AUFBAU
        </span>
        <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

// ─── Kalender Nicht-Verbunden Hinweis (dezent, nur wenn nicht verbunden) ────

function KalenderNichtVerbundenHinweis({ isLoading }: { isLoading: boolean }) {
  if (isLoading) return null;
  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b border-amber-100 bg-amber-50">
      <WifiOff size={13} className="text-amber-500 flex-shrink-0" />
      <span className="text-xs text-amber-700">Kalender nicht synchronisiert</span>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function WerkstattKiosk() {
  const [, setLocation] = useLocation();
  const [uhrzeit, setUhrzeit] = useState(new Date());
  const [collapsedWidgets, setCollapsedWidgets] = useState<Record<string, boolean>>({});

  // Google Calendar Hook
  const {
    isConnected,
    isLoading: gcalLoading,
    events: googleEvents,
    fetchEvents,
    updateEvent: gcalUpdateEvent,
    deleteEvent: gcalDeleteEvent,
  } = useGoogleCalendar();

  // Termin Modal State
  const [selectedTermin, setSelectedTermin] = useState<KalenderTermin | null>(null);
  const [editTermin, setEditTermin] = useState<KalenderTermin | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Refetch Supabase termine
  const [terminRefreshKey, setTerminRefreshKey] = useState(0);
  const refetchTermine = useCallback(() => setTerminRefreshKey(k => k + 1), []);

  // Refresh Google events
  const refreshGoogleEvents = useCallback(async () => {
    if (isConnected) {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
      await fetchEvents(start.toISOString(), end.toISOString()).catch(() => {});
    }
  }, [isConnected, fetchEvents]);

  // Handle termin save (edit)
  const handleSaveTermin = useCallback(async (updated: { id: string; titel: string; datum: string; startStunde: number; dauerStunden: number; googleEvent?: boolean }) => {
    try {
      if (updated.googleEvent) {
        const [sh, sm] = [Math.floor(updated.startStunde), Math.round((updated.startStunde % 1) * 60)];
        const endTotal = updated.startStunde + updated.dauerStunden;
        const [eh, em] = [Math.floor(endTotal), Math.round((endTotal % 1) * 60)];
        const startISO = new Date(`${updated.datum}T${sh.toString().padStart(2,'0')}:${sm.toString().padStart(2,'0')}:00`).toISOString();
        const endISO = new Date(`${updated.datum}T${eh.toString().padStart(2,'0')}:${em.toString().padStart(2,'0')}:00`).toISOString();
        await gcalUpdateEvent(updated.id, { summary: updated.titel, start: startISO, end: endISO });
        await refreshGoogleEvents();
      } else {
        const [sh, sm] = [Math.floor(updated.startStunde), Math.round((updated.startStunde % 1) * 60)];
        const endTotal = updated.startStunde + updated.dauerStunden;
        const [eh, em] = [Math.floor(endTotal), Math.round((endTotal % 1) * 60)];
        const beginn = `${updated.datum}T${sh.toString().padStart(2,'0')}:${sm.toString().padStart(2,'0')}:00`;
        const ende = `${updated.datum}T${eh.toString().padStart(2,'0')}:${em.toString().padStart(2,'0')}:00`;
        await supabase.from('termine').update({ titel: updated.titel, beginn, ende }).eq('id', updated.id);
        refetchTermine();
      }
      toast.success('Termin aktualisiert');
      setEditTermin(null);
    } catch {
      toast.error('Termin konnte nicht gespeichert werden');
    }
  }, [gcalUpdateEvent, refreshGoogleEvents, refetchTermine]);

  // Handle termin delete
  const handleDeleteTermin = useCallback(async (termin: KalenderTermin) => {
    const confirmed = window.confirm(`Termin "${termin.titel}" wirklich l\u00f6schen?`);
    if (!confirmed) return;
    try {
      if (termin.googleEvent) {
        await gcalDeleteEvent(termin.id);
        await refreshGoogleEvents();
      } else {
        await supabase.from('termine').delete().eq('id', termin.id);
        refetchTermine();
      }
      setSelectedTermin(null);
      toast.success('Termin gel\u00f6scht');
    } catch {
      toast.error('Termin konnte nicht gel\u00f6scht werden');
    }
  }, [gcalDeleteEvent, refreshGoogleEvents, refetchTermine]);

  // Handle termin create
  const handleCreateTermin = useCallback(async (data: { titel: string; datum: string; startStunde: number; dauerStunden: number }) => {
    try {
      const [sh, sm] = [Math.floor(data.startStunde), Math.round((data.startStunde % 1) * 60)];
      const endTotal = data.startStunde + data.dauerStunden;
      const [eh, em] = [Math.floor(endTotal), Math.round((endTotal % 1) * 60)];
      const beginn = `${data.datum}T${sh.toString().padStart(2,'0')}:${sm.toString().padStart(2,'0')}:00`;
      const ende = `${data.datum}T${eh.toString().padStart(2,'0')}:${em.toString().padStart(2,'0')}:00`;

      // Always create in Supabase for Werkstatt kiosk
      await supabase.from('termine').insert({ titel: data.titel, beginn, ende, business_unit: 'WK', ganztaegig: false });
      refetchTermine();
      toast.success('Termin erstellt');
      setShowCreateModal(false);
    } catch {
      toast.error('Termin konnte nicht erstellt werden');
    }
  }, [isConnected, refreshGoogleEvents, refetchTermine]);

  // Live-Uhrzeit
  useEffect(() => {
    const interval = setInterval(() => setUhrzeit(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Google Calendar events when connected
  useEffect(() => {
    if (isConnected) {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
      fetchEvents(start.toISOString(), end.toISOString()).catch(() => {});
    }
  }, [isConnected, fetchEvents]);

  const toggleCollapse = (key: string) => {
    setCollapsedWidgets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Counts (from real data)
  const todayStr = localIsoDate(new Date());
  const [offenCount, setOffenCount] = useState(0);
  const [heuteCount, setHeuteCount] = useState(0);
  useEffect(() => {
    supabase.from('akten').select('id', { count: 'exact', head: true })
      .eq('business_unit', 'WK').eq('status', 'offen').eq('geloescht', false)
      .then(({ count }) => setOffenCount(count || 0));
    supabase.from('termine')
      .select('id', { count: 'exact', head: true })
      .eq('business_unit', 'WK')
      .gte('beginn', todayStr + 'T00:00:00')
      .lte('beginn', todayStr + 'T23:59:59')
      .then(({ count }) => setHeuteCount((count || 0) + googleEvents.filter(e => e.start.slice(0, 10) === todayStr).length));
  }, [todayStr, googleEvents]);

  // Format Uhrzeit
  const uhrzeitStr = uhrzeit.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: '#EAECEF' }}>
      {/* ═══ HEADER ═══ */}
      <header className="flex flex-col sm:flex-row items-center justify-between px-5 py-2 sm:py-0 bg-white shadow-sm border-b border-gray-200 flex-shrink-0 gap-2 sm:gap-0" style={{ minHeight: '64px' }}>
        {/* Links: Logo + Titel */}
        <div className="flex items-center gap-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-base"
            style={{ backgroundColor: KOOP_ROT }}
          >
            K
          </div>
          <div>
            <h1 className="text-base font-bold text-gray-800">Werkstatt Cockpit</h1>
            <p className="text-xs text-gray-400">SV Büro Koop</p>
          </div>
        </div>

        {/* Mitte: Uhrzeit + Datum */}
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-800 font-mono tracking-wider">{uhrzeitStr}</div>
          <div className="text-xs text-gray-400">{formatDate(uhrzeit)}</div>
        </div>

        {/* Rechts: Badges + Close */}
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-gray-100 text-gray-600" style={{ height: '40px' }}>
            <Calendar size={16} />
            {heuteCount} Heute
          </span>
          <span
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold"
            style={{
              height: '40px',
              backgroundColor: offenCount > 0 ? '#FEF3C7' : '#D1FAE5',
              color: offenCount > 0 ? '#D97706' : '#059669',
            }}
          >
            <FileText size={16} />
            {offenCount} Offen
          </span>
          <button
            onClick={() => setLocation('/einstellungen/integrationen')}
            className="p-2.5 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            title="Einstellungen"
          >
            <Settings size={20} />
          </button>
          <button
            onClick={() => setLocation('/')}
            className="p-2.5 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors ml-1"
            title="Cockpit schließen"
          >
            <X size={22} />
          </button>
        </div>
      </header>

      {/* ═══ WIDGET GRID ═══ */}
      <main className="flex-1 overflow-hidden p-4">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_300px] gap-4 h-full overflow-y-auto lg:overflow-hidden">

          {/* ── Spalte 1: Fallübersicht ── */}
          <div className="flex flex-col gap-4">
            <WidgetBox
              title="Fallübersicht"
              icon={<FileText size={18} />}
              collapsed={collapsedWidgets['fall']}
              onToggleCollapse={() => toggleCollapse('fall')}
              className="flex-1"
              headerRight={
                <button onClick={() => window.location.reload()} className="p-1.5 rounded hover:bg-gray-100 text-gray-400" title="Aktualisieren"><RefreshCw size={14} /></button>
              }
            >
              <FalluebersichtWidget />
            </WidgetBox>
          </div>

          {/* ── Spalte 2: Kalender + Aufgaben ── */}
          <div className="flex flex-col gap-4">
            <WidgetBox
              title="Kalender"
              icon={<Calendar size={18} />}
              collapsed={collapsedWidgets['kalender']}
              onToggleCollapse={() => toggleCollapse('kalender')}
              className="flex-1"
              headerRight={
                <button
                  className="flex items-center gap-2 px-3 rounded-lg text-sm font-bold text-white transition-colors hover:opacity-90"
                  style={{ backgroundColor: KOOP_ROT, height: '36px' }}
                  onClick={() => setShowCreateModal(true)}
                >
                  <Plus size={16} /> Neuer Fall
                </button>
              }
            >
              {/* Dezenter Hinweis wenn Kalender nicht verbunden */}
              {!isConnected && <KalenderNichtVerbundenHinweis isLoading={gcalLoading} />}
              <KalenderWidget googleEvents={googleEvents} onEventClick={setSelectedTermin} />
            </WidgetBox>

            <WidgetBox
              title="Aufgaben"
              icon={<ListTodo size={18} />}
              collapsed={collapsedWidgets['aufgaben']}
              onToggleCollapse={() => toggleCollapse('aufgaben')}
              headerRight={
                <span className="text-sm text-gray-400 mr-2 font-medium">
                  {(() => { try { const s = localStorage.getItem('werkstatt_aufgaben'); return s ? JSON.parse(s).filter((a: Aufgabe) => !a.erledigt).length : 0; } catch { return 0; } })()} offen
                </span>
              }
            >
              <AufgabenWidget />
            </WidgetBox>
          </div>

          {/* ── Spalte 3: Mietwagen + Autoglas ── */}
          <div className="flex flex-col gap-4">
            <WidgetBox
              title="Mietwagen"
              icon={<Car size={18} />}
              collapsed={collapsedWidgets['mietwagen']}
              onToggleCollapse={() => toggleCollapse('mietwagen')}
              className="flex-1"
            >
              <ImAufbauWidget
                title="Mietwagen"
                description="Mietwagen-Modul wird eingerichtet. Anbindung an MietGo / Rentsoft in Planung."
              />
            </WidgetBox>

            <WidgetBox
              title="Autoglas (Lenneglas)"
              icon={<GlassWater size={18} />}
              collapsed={collapsedWidgets['autoglas']}
              onToggleCollapse={() => toggleCollapse('autoglas')}
              className="flex-1"
            >
              <ImAufbauWidget
                title="Autoglas (Lenneglas)"
                description="Autoglas-Modul wird eingerichtet. Anbindung an Lenneglas in Planung."
              />
            </WidgetBox>
          </div>
        </div>
      </main>

      {/* Termin Modals */}
      {selectedTermin && (
        <TerminDetailModal
          termin={selectedTermin}
          onClose={() => setSelectedTermin(null)}
          onEdit={(t) => { setSelectedTermin(null); setEditTermin(t); }}
          onDelete={handleDeleteTermin}
        />
      )}
      {editTermin && (
        <TerminEditModal
          termin={editTermin}
          onClose={() => setEditTermin(null)}
          onSave={handleSaveTermin}
        />
      )}
      {showCreateModal && (
        <TerminCreateModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateTermin}
        />
      )}
    </div>
  );
}
